/**
 * The ten seconds a vote is open, drawn twice: as a bar that slides and as a number that counts.
 *
 * **ITS OWN MODULE BECAUSE TWO CARDS DRAW IT AND ONLY ONE OF THEM IS THE BALLOT.** It began
 * inside `./VoteCard.tsx`; the star's taker's-choice card runs on the *same* ten seconds — a
 * star opens an ordinary room-wide vote and its taker answers on the same deadline as everybody
 * else — so a second copy of the arithmetic and the timer would be two things to keep in step
 * over one number. `rocket-pack.ts` importing the rocket's own constants, one layer up.
 *
 * **THE DEADLINE IS THE ROOM'S AND THE CLOCK IS THE BROWSER'S.** `serverVoteSchema.endsAtTick` is
 * a tick and `GameConnection` turns it into a wall-clock instant once (`VoteView.endsAtMs`), so
 * nothing here counts anything down against a local guess and a card that re-mounts mid-vote
 * still says what the room believes. `inventory-strip.ts`' rule: a second copy of a value that is
 * already correct is a second copy that can be wrong.
 */

import { useEffect, useRef, useState } from 'react';
import { voteSecondsLeft } from './vote-card';

/**
 * The bar.
 *
 * It reuses the duel's `chompdome-duel-countdown` keyframe rather than declaring a second,
 * identical one: it is *the* countdown animation in this client, and two copies under two names
 * would be two things to keep in step. The duration is captured on mount and never recomputed,
 * for `DuelOverlay.Countdown`'s reason: a card re-renders whenever an answer lands, and writing a
 * fresh `animation` on one of those renders would restart the bar from full, which reads as the
 * game handing back time the player no longer has.
 *
 * Battery saver drops the animation and keeps the bar, full: the seconds beside it are still
 * counting and still exact, so what is lost is the movement rather than the deadline.
 */
export function VoteCountdown({
  endsAtMs,
  now,
  still,
}: {
  endsAtMs: number;
  now: () => number;
  still: boolean;
}) {
  const [durationMs] = useState(() => Math.max(endsAtMs - now(), 1));

  return (
    <div className="h-1 flex-1 overflow-hidden rounded-full bg-border" aria-hidden>
      <div
        data-testid="vote-countdown"
        className="h-full origin-left bg-primary"
        style={
          still
            ? undefined
            : { animation: `chompdome-duel-countdown ${durationMs}ms linear forwards` }
        }
      />
    </div>
  );
}

/**
 * The seconds left, as a number that actually counts down.
 *
 * `DuelOverlay.CountdownSeconds`' arrangement, and the same bound on what it costs: it wakes on
 * each whole-second boundary of its own deadline — ten times over a whole vote, not twenty times
 * a second — and it is a leaf, so what re-renders is one span and nothing above it. The bar
 * beside it is CSS and does the smooth part.
 *
 * The arithmetic is `voteSecondsLeft` in `./vote-card.ts` rather than inline, so what the card
 * says at the boundary and past the deadline is a thing a test asserts.
 */
export function VoteSeconds({
  endsAtMs,
  now,
  what = 'to answer',
}: {
  endsAtMs: number;
  now: () => number;
  /**
   * What the seconds are left FOR, as the tail of the accessible label.
   *
   * A parameter rather than a fixed sentence because this component has a third mount site now: a
   * deathmatch lobby counts down to a round starting rather than to an answer being taken, and a
   * screen reader told "seconds left to answer" over a card with nothing to answer would be the
   * one thing about reusing the bar that was not free. The default is what the two vote cards
   * already said, so neither of them changed.
   */
  what?: string;
}) {
  const nowRef = useRef(now);
  nowRef.current = now;
  const [seconds, setSeconds] = useState(() => voteSecondsLeft(endsAtMs, now()));

  useEffect(() => {
    let handle: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      const left = Math.max(0, endsAtMs - nowRef.current());
      setSeconds(voteSecondsLeft(endsAtMs, nowRef.current()));
      if (left <= 0) return;
      // The next boundary rather than a fixed interval: the number is a `ceil`, so it changes
      // when the remainder runs out and at no other moment.
      handle = setTimeout(schedule, left % 1000 === 0 ? 1000 : left % 1000);
    };

    schedule();
    return () => clearTimeout(handle);
  }, [endsAtMs]);

  return (
    <span
      role="timer"
      aria-live="off"
      aria-label={`${seconds} seconds left ${what}`}
      data-testid="vote-seconds"
      className={`w-5 shrink-0 text-right text-[11px] font-black tabular-nums ${
        seconds <= 3 ? 'text-destructive' : 'text-muted-foreground'
      }`}
    >
      {seconds}
    </span>
  );
}
